import React, { useEffect, useRef, useSyncExternalStore } from 'react';

import { useBlocker, useRouter } from '@tanstack/react-router';

import { GenericDialog } from '@/components/NewUI/Components/Modals';

import type { AnyRouter } from '@tanstack/react-router';
import type { ReactElement, RefObject } from 'react';

export interface LeaveGuardLocation {
  pathname: string;
  search: Record<string, unknown>;
}

export interface LeaveGuardOptions {
  /** Client navigations that keep the user's work, such as a page's own view toggle in the URL. */
  ignore?: (move: { from: LeaveGuardLocation; to: LeaveGuardLocation }) => boolean;
}

interface LeaveGuardProps {
  isDirty: boolean;
  allowLeave?: RefObject<boolean>;
  ignore?: RefObject<LeaveGuardOptions['ignore']>;
}

const toLeaveGuardLocation = ({
  pathname,
  search
}: {
  pathname: string;
  search: unknown;
}): LeaveGuardLocation => ({ pathname, search: (search ?? {}) as Record<string, unknown> });

// One token per mounted guard (or useReportUnsavedChanges) with unsaved changes, so the shell can
// ask "is anything dirty?" without each page lifting its state. Guards on pages mounted from HAML
// write here too, harmlessly.
const dirtyGuards = new Set<symbol>();
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

export const hasUnsavedChanges = (): boolean => dirtyGuards.size > 0;

export const subscribeUnsavedChanges = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useHasUnsavedChanges = (): boolean =>
  useSyncExternalStore(subscribeUnsavedChanges, hasUnsavedChanges);

/** Counts one source of unsaved changes until the returned function runs. */
export const markUnsavedChanges = (): (() => void) => {
  const token = Symbol('unsaved changes');
  dirtyGuards.add(token);
  notify();
  return () => {
    dirtyGuards.delete(token);
    notify();
  };
};

// useBlocker throws outside a RouterProvider, so HAML mounts omit this component.
const ClientLeaveDialog = ({ allowLeave, ignore, isDirty }: LeaveGuardProps) => {
  const { proceed, reset, status } = useBlocker({
    shouldBlockFn: ({ current, next }) =>
      !allowLeave?.current &&
      !ignore?.current?.({ from: toLeaveGuardLocation(current), to: toLeaveGuardLocation(next) }),
    disabled: !isDirty,
    enableBeforeUnload: false,
    withResolver: true
  });
  return (
    <GenericDialog
      buttonCallback={() => proceed?.()}
      buttonText="Yes, leave"
      callbackLoading={false}
      description="Are you sure you want to leave the page? Any unsaved changes will be lost."
      isDialogOpen={status === 'blocked'}
      primaryButtonId="unsaved-changes-leave-button"
      secondaryButtonId="unsaved-changes-stay-button"
      secondaryButtonText="Back to editing"
      setDialogOpen={() => reset?.()}
      title="Leave page?"
      url=""
    />
  );
};

/**
 * Asks before a page with unsaved changes is left: `beforeunload` for a document load, and inside
 * the shell's router a blocker for client navigation, so the route can declare `leave: 'client'`.
 * Render the returned element: it holds the blocker and its
 * dialog, and is null outside a router (a page mounted from HAML). A page that confirms a leave
 * in its own dialog, or leaves straight after a save, sets `allowLeave.current` first so it is
 * not asked again. `options.ignore` lets through client navigations that keep the page's work.
 */
export const useUnsavedChangesGuard = (
  isDirty: boolean,
  allowLeave?: RefObject<boolean>,
  options: LeaveGuardOptions = {}
): ReactElement | null => {
  const router = useRouter<AnyRouter>({ warn: false });
  // A ref, so the blocker reads the latest predicate without re-registering.
  const ignore = useRef(options.ignore);
  ignore.current = options.ignore;
  useEffect(() => {
    if (!isDirty) return undefined;
    const unmark = markUnsavedChanges();
    const prompt = (event: BeforeUnloadEvent) => {
      if (allowLeave?.current) return;
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign -- the browser's beforeunload contract
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', prompt);
    return () => {
      window.removeEventListener('beforeunload', prompt);
      unmark();
    };
  }, [allowLeave, isDirty]);
  return router ? (
    <ClientLeaveDialog allowLeave={allowLeave} ignore={ignore} isDirty={isDirty} />
  ) : null;
};
